import { useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { OfferType, Subscription, useOffers } from '@app/api';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  table: {
    backgroundColor: theme.colors.grayBackground,
    width: '100%',
    marginTop: theme.marginBase * 2,
    border: {
      color: theme.colors.grayBorder,
      style: 'solid',
      width: 1,
    },
    borderRadius: theme.borderRadius.std,
  },
  line: {
    borderBottom: {
      color: theme.colors.grayBorder,
      style: 'solid',
      width: 1,
    },
  },
  label: {
    ...theme.fonts.label,
    padding: theme.marginBase,
  },
  rightCell: {
    ...theme.fonts.body,
    padding: theme.marginBase,
    textAlign: 'right',
  },
}));

interface Props {
  subscription?: Subscription;
}

export const SubscriptionDetails = ({ subscription }: Props) => {
  const classes = useStyles();

  const { data: offers } = useOffers();
  const { i18n, t } = useTranslation();

  const offer = useMemo(() => {
    if (!offers) {
      return null;
    }
    if (!subscription) {
      return offers.find((o) => {
        return o.type === OfferType.SUBSCRIPTION && o.defaultSubscription;
      });
    }
    return subscription.offer;
  }, [offers, subscription]);

  return (
    <table className={classes.table}>
      <tbody>
        <tr className={classes.line}>
          <td className={classes.label}>{t('billing.subscription.name')}</td>
          <td className={classes.rightCell}>{offer?.name}</td>
        </tr>
        <tr className={classes.line}>
          <td className={classes.label}>
            {t('billing.subscription.statusLabel')}
          </td>
          <td className={classes.rightCell}>
            {t('billing.subscription.status', {
              context: subscription?.status || 'active',
            })}
          </td>
        </tr>
        {subscription?.cancelAt ? (
          <tr className={classes.line}>
            <td className={classes.label}>
              {t('billing.subscription.cancelAt')}
            </td>
            <td className={classes.rightCell}>
              {subscription
                ? Intl.DateTimeFormat(i18n.language, {
                    dateStyle: 'medium',
                  }).format(new Date(subscription.cancelAt))
                : '/'}
            </td>
          </tr>
        ) : (
          <tr className={classes.line}>
            <td className={classes.label}>
              {t('billing.subscription.expirationDate', {
                context: subscription?.status,
              })}
            </td>
            <td className={classes.rightCell}>
              {subscription
                ? Intl.DateTimeFormat(i18n.language, {
                    dateStyle: 'medium',
                  }).format(new Date(subscription.expirationDate))
                : '/'}
            </td>
          </tr>
        )}
        {!!subscription?.products?.length && (
          <tr className={classes.line}>
            <td className={classes.label}>
              {t('billing.subscription.products')}
            </td>
            <td className={classes.rightCell}>
              {subscription.products.map((product) => (
                <div key={product.id}>{product.title}</div>
              ))}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};
